'use client';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormControl, FormField, FormItem } from '@/components/ui/form';
import useRegisterStore from '@/stores/register';
import useRegisterPageFactory from '@/components/RegisterPage/Factory';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useCallback, useState, useTransition, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import type { RegisterFormData } from '@/stores/register/types';
import Image from 'next/image';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/toast';
import { checkEmailExistence } from '@/app/(auth)/actions';
import useRudderStackAnalytics from '@/hooks/useRudderAnalytics';
import useIsWebview from '@/hooks/use-is-webview';
import { useSearchParams } from 'next/navigation';
import RegisterFormSchema from '@/components/RegisterPage/schama';
import ErrorMessage from '@/components/RegisterPage/ErrorMessage';
import { checkEmailDomain, type EmailDomainSuggestion } from '@/utils/emailDomainValidator';
import { EmailDomainAutocomplete } from '@/components/email-domain-autocomplete';
const RegisterFormStep = () => {
  const {
    previousStep,
    nextStep,
    formData,
    toggleShowPassword,
    showPassword
  } = useRegisterStore(['previousStep', 'nextStep', 'formData', 'toggleShowPassword', 'showPassword']);
  useRegisterPageFactory();
  const form = useFormContext<RegisterFormData>();
  const analytics = useRudderStackAnalytics();
  const [isWebView] = useIsWebview();
  const searchParams = useSearchParams();
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingEmail, startCheckingEmail] = useTransition();
  const hasTrackedPageView = useRef(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [emailSuggestion, setEmailSuggestion] = useState<EmailDomainSuggestion | null>(null);
  const hasShownSuggestion = useRef(false);
  useEffect(() => {
    const subscription = form.watch((_values, {
      name
    }) => {
      if (name && errors[name]) {
        setErrors(prev => {
          const next = {
            ...prev
          };
          delete next[name];
          return next;
        });
      }
      if (name === 'email') {
        setEmailSuggestion(null);
        hasShownSuggestion.current = false;
      }
    });
    return () => subscription.unsubscribe();
  }, [form, errors]);

  // Check if this is a social auth registration (skipPassword=true)
  const skipPassword = searchParams.get('skipPassword') === 'true';
  const isSocialAuth = skipPassword && !!searchParams.get('provider');
  const phoneNumber = formData.phoneNumber || '';
  const passwordValue = form.watch('password');
  const passwordConfirmationValue = form.watch('passwordConfirmation');
  const isPasswordValid = skipPassword || passwordValue && passwordValue.length >= 8;
  const passwordsMatch = skipPassword || passwordValue && passwordConfirmationValue && passwordValue === passwordConfirmationValue;
  const showPasswordError = !skipPassword && passwordConfirmationValue && passwordValue && !passwordsMatch;

  // Check for domain typos on mount (email may come pre-filled from URL params)
  useEffect(() => {
    const email = form.getValues('email');
    if (email) {
      const suggestion = checkEmailDomain(email);
      setEmailSuggestion(suggestion.hasTypo ? suggestion : null);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEmailBlur = useCallback(() => {
    const email_0 = form.getValues('email');
    if (!email_0) {
      setEmailSuggestion(null);
      return;
    }
    const suggestion_0 = checkEmailDomain(email_0);
    setEmailSuggestion(suggestion_0.hasTypo ? suggestion_0 : null);
  }, [form]);
  const acceptEmailSuggestion = useCallback(() => {
    if (emailSuggestion?.suggested) {
      form.setValue('email', emailSuggestion.suggested, {
        shouldValidate: true
      });
      setEmailSuggestion(null);
    }
  }, [form, emailSuggestion]);
  const onSubmit = useCallback(async () => {
    // First submit with a typo: show suggestion and stop.
    // Second submit (user saw it and clicked again): let through.
    // Skip entirely for social auth — email is provider-verified.
    if (!isSocialAuth) {
      const emailValue = form.getValues('email');
      const suggestion_1 = checkEmailDomain(emailValue);
      if (suggestion_1.hasTypo && !hasShownSuggestion.current) {
        setEmailSuggestion(suggestion_1);
        hasShownSuggestion.current = true;
        return;
      }
    }
    setIsSubmitting(true);
    const values = form.getValues();
    const schema = RegisterFormSchema(isWebView, skipPassword);
    const result = schema.safeParse(values);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as string;
        if (['email', 'password', 'passwordConfirmation'].includes(field) && !fieldErrors[field]) {
          fieldErrors[field] = issue.message;
        }
      }
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
        setIsSubmitting(false);
        return;
      }
    }
    setErrors({});
    const email_1 = values.email;
    const emailFormData = new FormData();
    emailFormData.append('email', email_1);
    startCheckingEmail(async () => {
      const result_0 = await checkEmailExistence({
        status: 'idle',
        exists: false
      }, emailFormData);
      if (result_0.status === 'invalid_data') {
        toast({
          type: 'error',
          description: 'Email inválido'
        });
        setIsSubmitting(false);
        return;
      }
      if (result_0.status === 'failed') {
        toast({
          type: 'error',
          description: 'Erro ao verificar email. Tente novamente.'
        });
        setIsSubmitting(false);
        return;
      }
      if (result_0.exists) {
        toast({
          type: 'error',
          description: 'Já existe uma conta com esse email'
        });
        setIsSubmitting(false);
        return;
      }
      nextStep();
      setIsSubmitting(false);
    });
  }, [form, nextStep, skipPassword, isWebView]);
  useEffect(() => {
    if (!analytics || hasTrackedPageView.current) return;
    hasTrackedPageView.current = true;
    analytics.track('Register Step', {
      step: 'register_form',
      platform: isWebView ? 'app' : 'web'
    });
  }, [analytics, isWebView]);
  return <div className="min-h-screen w-full flex flex-col px-4 py-8 md:max-w-lg md:mx-auto md:py-20">
      <div className="flex-1 flex flex-col justify-between md:justify-start gap-4 w-full">
        <div className="flex flex-col gap-8">
          <Button type="button" variant="none" size="iconArea" onClick={previousStep} className="text-primary md:hidden hover:text-primary/80 transition-colors self-start">
            <ArrowLeft className="size-5" />
          </Button>

          <div className="flex flex-col gap-2 items-center">
            <h1 className="text-primary text-2xl font-bold">Comece por aqui</h1>
            <p className="text-muted-foreground text-sm">
              Só mais alguns dados e você está dentro
            </p>
          </div>

          <div className="flex flex-col gap-6">
            <FormField control={form.control} name="email" render={({
            field: field_0
          }) => <FormItem>
                  <div className="relative">
                    <FormControl>
                      <Input {...field_0} type="email" inputSize="xl" rounded="2xl" variant="outline" inputMode="email" autoComplete="off" floatingLabel label="E-mail" disabled={isSocialAuth} className={isSocialAuth ? 'opacity-50 cursor-not-allowed pr-4' : 'pr-12'} onBlur={() => {
                  field_0.onBlur();
                  handleEmailBlur();
                }} />
                    </FormControl>
                    {!isSocialAuth && <EmailDomainAutocomplete email={field_0.value || ''} onSelect={completed => {
                form.setValue('email', completed, {
                  shouldValidate: true
                });
                setEmailSuggestion(null);
              }} />}
                  </div>
                  <ErrorMessage message={errors.email} />
                  {emailSuggestion && !isSocialAuth && <button type="button" onClick={acceptEmailSuggestion} className="mt-1 pl-4 text-sm text-muted-foreground hover:text-primary transition-colors text-left w-full">
                      Você quis dizer:{' '}
                      <span className="font-semibold text-primary underline">
                        {emailSuggestion.suggested}
                      </span>
                      ?
                    </button>}
                </FormItem>} />

            <div className="relative">
              <Input value={phoneNumber} disabled inputSize="xl" rounded="2xl" variant="outline" className="pl-12 opacity-50 cursor-not-allowed" floatingLabel label="Número do WhatsApp" />
              <div className="w-5 h-4 flex justify-center items-center overflow-hidden absolute top-1/2 left-4 -translate-y-1/2">
                <Image src="/images/brasil-flag.svg" width={20} height={17} alt="Bandeira do brasil" className="w-5 h-4" />
              </div>
            </div>

            {/* Password fields - hidden for social auth */}
            {!skipPassword && <>
                <FormField control={form.control} name="password" render={({
              field: field_1
            }) => <FormItem>
                      <FormControl>
                        <div className="relative">
                          <Input {...field_1} type={showPassword ? 'text' : 'password'} inputSize="xl" rounded="2xl" variant="outline" className="pr-12" autoComplete="new-password" floatingLabel label="Senha" />
                          <button type="button" onClick={toggleShowPassword} className="absolute top-1/2 right-4 -translate-y-1/2 text-primary/50 hover:text-primary transition-colors">
                            {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                          </button>
                        </div>
                      </FormControl>
                      <ErrorMessage message={errors.password} />
                    </FormItem>} />

                <FormField control={form.control} name="passwordConfirmation" render={({
              field: field_2
            }) => <FormItem>
                      <FormControl>
                        <div className="relative">
                          <Input {...field_2} type={showPasswordConfirmation ? 'text' : 'password'} inputSize="xl" rounded="2xl" variant="outline" className="pr-12" autoComplete="new-password" floatingLabel label="Confirme sua senha" />
                          <button type="button" onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)} className="absolute top-1/2 right-4 -translate-y-1/2 text-primary/50 hover:text-primary transition-colors">
                            {showPasswordConfirmation ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                          </button>
                        </div>
                      </FormControl>
                      <ErrorMessage message={errors.passwordConfirmation} />
                      {showPasswordError && !errors.passwordConfirmation && <p className="text-sm font-medium text-destructive flex items-center gap-2 mt-2">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
                            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                            <path d="M8 4.5V8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            <circle cx="8" cy="11" r="0.75" fill="currentColor" />
                          </svg>
                          As senhas não coincidem
                        </p>}
                    </FormItem>} />

                {isPasswordValid && <div className="flex items-center gap-2 text-brand-bright-green text-sm">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
                      <path d="M13.3337 4L6.00033 11.3333L2.66699 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>Ao menos 8 caracteres</span>
                  </div>}
              </>}
          </div>
        </div>

        <Button type="button" onClick={onSubmit} disabled={isSubmitting || isCheckingEmail} size="xl" rounded="full" className="md:max-w-[240px] md:mx-auto  font-semibold w-full">
          {isSubmitting || isCheckingEmail ? <div className="flex items-center gap-2">
              <Spinner size="lg" spinnerClassName="bg-white dark:bg-black" />
              <span>
                {isCheckingEmail ? 'Verificando...' : 'Confirmando...'}
              </span>
            </div> : 'Confirmar'}
        </Button>
      </div>
    </div>;
};
export default RegisterFormStep;